import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core';
import RateCardIcon from '@material-ui/icons/AttachMoneySharp';
import {
  EditableCard2,
  Edit,
  EditContent,
  EditTitle,
  ReadOnly,
  ReadOnlyContent
} from '~/modules/common/components/EditableCard';
import {
  ProjectCostOverridesReadOnlyContent,
  ProjectCostOverridesDialogContent
} from './components';
import {
  useAvailableCostOverrideDimensions,
  useProjectCostOverrideDimensionHandlers
} from './hooks';

const avatar = <RateCardIcon />;

const useCardStyles = makeStyles(theme => ({
  card: {
    marginTop: theme.spacing(2)
  }
}));

const useEditContentStyles = makeStyles({
  noVerticalPadding: {
    paddingTop: 0,
    paddingBottom: 0
  }
});

const noop = () => {};

const getMaxWidth = (selectedDimensions, projectRates) => {
  const dimensionsCount = selectedDimensions?.length || 0;
  const hasEffectiveDate = projectRates.some(r => !r.isInitialRate);

  if (dimensionsCount > 2) {
    return 'xl';
  }

  if (dimensionsCount > 1) {
    return 'lg';
  }

  if (dimensionsCount === 0 && hasEffectiveDate) {
    return 'md';
  }

  if (dimensionsCount === 0) {
    return 'sm';
  }

  return 'md';
};

export const ProjectCostOverrides = ({
  editable = false,
  projectSlug,
  projectCurrency
}) => {
  const { formatMessage } = useIntl();
  const cardClasses = useCardStyles();
  const editContentClasses = useEditContentStyles();
  const editor = useProjectCostOverrideDimensionHandlers({ projectCurrency });
  const { availableDimensions } = useAvailableCostOverrideDimensions({
    selectedDimensions: editor.selectedDimensions
  });
  const maxWidth = getMaxWidth(editor.selectedDimensions, editor.projectRates);

  return (
    <EditableCard2
      editable={editable}
      classes={cardClasses}
      editContentClassName={editContentClasses.noVerticalPadding}
      maxWidth={maxWidth}
      ariaLabelKey="projectCostOverrides.cardTitle"
      dataQeId="ProjectCostOverridesCard"
    >
      <ReadOnly
        expandable={false}
        avatar={avatar}
        title={formatMessage({ id: 'projectCostOverrides.cardTitle' })}
      >
        <ReadOnlyContent>
          <ProjectCostOverridesReadOnlyContent />
        </ReadOnlyContent>
      </ReadOnly>
      <Edit onSave={noop} onCancel={editor.reset}>
        <EditTitle>
          {formatMessage({ id: 'projectCostOverrides.cardTitle' })}
        </EditTitle>
        <EditContent>
          <ProjectCostOverridesDialogContent
            projectSlug={projectSlug}
            projectCurrency={projectCurrency}
            editor={editor}
            availableDimensions={availableDimensions}
          />
        </EditContent>
      </Edit>
    </EditableCard2>
  );
};

ProjectCostOverrides.propTypes = {
  editable: PropTypes.bool,
  projectSlug: PropTypes.string,
  projectCurrency: PropTypes.object
};

export default ProjectCostOverrides;
